import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/hooks/useApi';
import type {
  CreatorRequest,
  CreatorRequestBatch,
  CreatorRequestBatchDetail,
  CreatorRequestBatchStatus,
  CreatorRequestComment,
} from '@/api/types';

const KEYS = {
  all: ['inviter', 'creator-request-batches'] as const,
  list: (params?: InviterBatchesParams) => [...KEYS.all, 'list', params] as const,
  detail: (publicId: string) => [...KEYS.all, 'detail', publicId] as const,
};

export interface InviterBatchesParams {
  [key: string]: string | number | boolean | undefined;
  status?: string; // comma-separated CreatorRequestBatchStatus list
  requesterExternalId?: string;
  page?: number;
  limit?: number;
}

export function useInviterCreatorRequestBatches(params: InviterBatchesParams = {}) {
  const api = useApi();
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () =>
      api.getPaged<CreatorRequestBatch[]>('/portal/creator-request-batches', params),
  });
}

export function useInviterCreatorRequestBatch(publicId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: publicId ? KEYS.detail(publicId) : KEYS.all,
    enabled: !!publicId,
    queryFn: () =>
      api.get<CreatorRequestBatchDetail>(`/portal/creator-request-batches/${publicId}`),
  });
}

interface ToggleResponse {
  item: CreatorRequest;
  batch: CreatorRequestBatchDetail | null;
}

export function useToggleBatchItemCheck() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      publicId,
      itemPublicId,
      checked,
    }: {
      publicId: string;
      itemPublicId: string;
      checked: boolean;
    }) =>
      api.patch<ToggleResponse>(
        `/portal/creator-request-batches/${publicId}/items/${itemPublicId}/check`,
        { checked },
      ),
    // Optimistic: flip the item's status locally so the checkbox feels
    // instant. We do NOT snapshot `previous` for rollback — rapid clicks
    // share this mutation instance and would clobber each other's
    // snapshots. Instead we rely on onSettled's invalidate to refetch the
    // authoritative state; a failed toggle briefly shows the optimistic
    // state and then snaps to the server truth once the refetch lands.
    onMutate: async (vars) => {
      const detailKey = KEYS.detail(vars.publicId);
      await qc.cancelQueries({ queryKey: detailKey });
      const current = qc.getQueryData<CreatorRequestBatchDetail>(detailKey);
      if (!current) return;
      const nextItems = current.items.map((it) =>
        it.publicId === vars.itemPublicId
          ? {
              ...it,
              status: vars.checked ? ('COMPLETED' as const) : ('REQUESTED' as const),
            }
          : it,
      );
      const doneCount = nextItems.filter(
        (it) => it.status === 'ROSTERED' || it.status === 'CANCELLED' || it.status === 'COMPLETED',
      ).length;
      qc.setQueryData<CreatorRequestBatchDetail>(detailKey, {
        ...current,
        items: nextItems,
        checkedCount: doneCount,
      });
    },
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.publicId) });
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

interface BulkToggleResponse {
  items: CreatorRequest[];
  batch: CreatorRequestBatchDetail | null;
}

export function useBulkToggleBatchItems() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      publicId,
      itemPublicIds,
      checked,
    }: {
      publicId: string;
      itemPublicIds: string[];
      checked: boolean;
    }) =>
      api.post<BulkToggleResponse>(
        `/portal/creator-request-batches/${publicId}/items/bulk-check`,
        { itemPublicIds, checked },
      ),
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.publicId) });
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useAddBatchComment() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, body }: { publicId: string; body: string }) =>
      api.post<CreatorRequestComment>(
        `/portal/creator-request-batches/${publicId}/comments`,
        { body },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.publicId) });
    },
  });
}

export { KEYS as CREATOR_REQUEST_BATCH_KEYS };
export type { CreatorRequestBatchStatus };
